'use strict'
var levels = require('./lib/levels')
var internals = require('./lib/internals')
var _cry = internals.cry
var _args = internals.argsToArray
// liste des renderers existants
var _renderers = {
  ansi: require('./lib/ansiRenderer'),
  text: require('./lib/textRenderer'),
  file: require('./lib/fileRenderer'),
  cordova: require('./lib/cordovaRenderer'),
  browser: require('./lib/browserRenderer'),
  remote: require('./lib/remoteRenderer')
}
// var globales avec le channelName en clé
var _ticks = {}
var _configs = {}
var _loggers = {} // assure le singleton de chaque logger
// liste des renderers utilisable dans notre contexte d'exécution
var _suitableRenderers

/**
 * Retourne un logLevel valide
 * @param {string|number} logLevel
 * @return {number}
 * @private
 */
function _cleanLogLevel (logLevel) {
  if (typeof logLevel === 'number') return logLevel
  else if (typeof logLevel === 'string') return levels.fromString(logLevel)
  _cry(new Error('logLevel n’accepte que des string ou number'), logLevel)
  return levels.INFO
}

/**
 * Complète la conf avec le minimum requis (en prenant le 1er renderer dispo si y'en a aucun de précisé)
 * @param name
 * @private
 */
function _completeConfig(name) {
  if (!_configs[name]) _configs[name] = {}
  var _config = _configs[name]
  _config.name = name
  _ticks[name] = Date.now()

  // les renderers
  // on accepte la propriété au singulier
  if (_config.renderer) {
    _config.renderers = [_config.renderer]
    delete _config.renderer
  }

  // si y'a un logLevel général pour ce channel on le vérifie
  if (_config.hasOwnProperty('logLevel')) _config.logLevel = _cleanLogLevel(_config.logLevel)

  // on build les renderers si y'en a
  if (_config.renderers) {
    // si on est pas dans un contexte nodeJs avec stdout tty,
    // on remplace le renderer ansi par un renderer text
    // (inutile d'ajouter les préfixes colorés si c'est pas pour un écran)
    // if (globalThis.process?.stdout?.isTTY && _config.renderers.some(r => r.name === 'ansi')) {
    // webpack plante sur la ligne précédente, on revient à l'ancienne
    if (globalThis.process && globalThis.process.stdout && globalThis.process.stdout.isTTY && _config.renderers.some(r => r.name === 'ansi')) {
      if (_config.renderers.some(r => r.name === 'text')) {
        // on vire le renderer ansi, mais c'est bizarre…
        console.warn(`an-log: le channel ${name} a un renderer ansi et un autre text, on est dans un contexte sans tty on vire le renderere ansi`)
        _config.renderers = _config.renderers.filter(r => r.name !== 'ansi')
      } else {
        // on remplace
        for (const r of _config.renderers) {
          if (r.name === 'ansi') r.name = 'text'
        }
      }
    }

    _config.renderers = _config.renderers.map(function (rendererConfig) {
      if (typeof rendererConfig === 'function') {
        return rendererConfig // déjà buildé
      } else if (typeof rendererConfig === 'string') {
        rendererConfig = {name: rendererConfig, logLevel: levels.INFO}
      } else {
        // un objet, on fixe logLevel
        if (rendererConfig.hasOwnProperty('logLevel')) rendererConfig.logLevel = _cleanLogLevel(rendererConfig.logLevel)
        else if (_config.hasOwnProperty('logLevel')) rendererConfig.logLevel = _config.logLevel
        else rendererConfig.logLevel = levels.INFO
      }
      // on build
      if (_suitableRenderers[rendererConfig.name]) {
        return _suitableRenderers[rendererConfig.name].build(rendererConfig);
      } else {
        _cry(new Error('Unknown renderer ' + rendererConfig.name))
      }
    }).filter(function(renderer) {
      return typeof renderer === 'function'
    })
  } else {
    _config.renderers = []
  }
  // si y'en avait pas ou qu'il en reste pas on prend le 1er qu'on trouve
  if (!_config.renderers.length) {
    for (var r in _suitableRenderers) {
      _config.renderers.push(_suitableRenderers[r].build({}))
      break
    }
  }
}

/**
 * Retourne la conf courante
 * @param name
 * @returns {Object}
 * @private
 */
function _getConfig(name) {
  if (!_configs[name]) _completeConfig(name)
  return _configs[name]
}

/**
 * Initialise _renderers et _suitableRenderers
 * @private
 */
function _initialize () {
  if (_suitableRenderers) return // déjà initialisé
  _suitableRenderers = {}
  if (typeof window !== 'undefined') {
    if (typeof console.table === 'undefined') _suitableRenderers.cordova = _renderers.cordova
    else _suitableRenderers.browser = _renderers.browser
  } else if (typeof process !== 'undefined') {
    var tty = require('tty')
    if (tty.isatty(1) || process.env.LOG_COLORS === 'true') {
      _suitableRenderers.ansi = _renderers.ansi
    } else {
      _suitableRenderers.ansi = _renderers.text
    }
    _suitableRenderers.text = _renderers.text
    _suitableRenderers.file = _renderers.file
  }
  if (typeof window !== 'undefined' && window.rlog) {
    _suitableRenderers.remote = _renderers.remote
  }
}

/**
 * Envoie args aux renderers de ce channel
 * @param name
 * @param level
 * @param args
 * @returns {*}
 * @private
 */
function _log (name, level, args) {
  var _config = _getConfig(name)
  if (_config.logLevel < level) return
  if (!_config.renderers.length) return _cry('No log renderer available for', name, 'with', args)
  var newTick = Date.now()
  var record = {
    timestamp: new Date(),
    delay: newTick - _ticks[name],
    level: level,
    name: name,
    args: args
  }
  // si info le 1er argument est pris comme record.verb (et retirés des arguments)
  if (record.level === levels.INFO && args.length > 0) {
    record.verb = args.shift()
  } else if (record.level === levels.ERROR) {
    // on remplace l'argument par sa propriété stack s'il en a une
    for(var i = 0; i < args.length; i++) {
      // si c'est une erreur avec une cause, c'est la cause qui nous intéresse
      while (args[i] && args[i].cause) args[i] = args[i].cause
      if (args[i] && args[i].stack) record.args[i] = args[i].stack
    }
  }
  _ticks[name] = newTick
  _config.renderers.forEach(function(renderer) {
    renderer(record)
  })
}

/**
 * (re)Configure tous les loggers ou un seul
 * @typedef an-logger-configurator
 * @param {Object} data
 * @param {string} [channelName] Si fourni, data sera affecté seulement à ce channel en laissant les autres intact,
 *                               sinon data sera considéré comme une config globale (avec une clé par channel)
 */
function config (data, channelName) {
  if (channelName) {
    _configs[channelName] = data
    _completeConfig(channelName)
  } else {
    for (channelName in data) {
      config(data[channelName], channelName)
    }
  }
}

/**
 * Modifie le niveau de log (pour tous les renderers si c'est appelé sans rendererName ou après son build)
 * @param {string} channelName
 * @param {string|number} logLevel
 * @param {string} [rendererName] En préciser un pour n'affecter que celui-là
 */
function _setLogLevel (channelName, logLevel, rendererName) {
  if (typeof logLevel === 'string') logLevel = levels.fromString(logLevel)
  else if (typeof logLevel !== 'number') return _cry(new Error('setLogLevel wants string or number'))
  var _config = _getConfig(channelName)
  if (rendererName) {
    var renderer = _config.renderers.find(function (r) { return r.name === rendererName })
    if (!renderer) return _cry(new Error('Aucun renderer ' + rendererName))
    if (renderer.logLevel) {
      renderer.logLevel = logLevel
    } else {
      if (_config.renderers.length > 1) _log(channelName, levels.WARNING, 'renderer ' + rendererName + ' already built, setting logLevel ' + logLevel + ' to all renderers')
      _configs[channelName].logLevel = logLevel
    }
  } else {
    _configs[channelName].logLevel = logLevel
  }
}

/**
 * Retourne le logger pour un channel
 * @param {string} name Le channel de log voulu
 * @param {an-logger-config} configOptions
 * @returns {an-logger}
 */
function getLogger(name, configOptions) {
  function logger () {
    _log(name, levels.INFO, _args(arguments))
  }
  if (!name || typeof name !== 'string') return _cry(new Error('logger name is mandatory'))
  // s'il existe pour ce channel on le retourne
  if (_loggers[name]) return _loggers[name]
  // sinon on le construit
  if (configOptions) config(configOptions, name)
  logger.config = function (configOptions) {
    config(configOptions, name)
  }
  logger.debug = function () {
    _log(name, levels.DEBUG, _args(arguments))
  }
  logger.info = function () {
    _log(name, levels.INFO, _args(arguments))
  }
  logger.warn = function () {
    _log(name, levels.WARNING, _args(arguments))
  }
  logger.error = function () {
    _log(name, levels.ERROR, _args(arguments))
  }
  logger.setLogLevel = function (logLevel) {
    _setLogLevel(name, logLevel)
  }
  _loggers[name] = logger

  return logger
}

getLogger.config = config
_initialize()

module.exports = getLogger

/**
 * Un logger. Sa fonction principale log si logLevel >= info
 * (modifier les has* ne change rien, pour changer le logLevel il faut appeler config)
 * @typedef an-logger
 * @type function
 * @property {function} config Pour modifier la config de ce logger
 * @property {function} debug Log si debug actif
 * @property {function} info Log si info actif
 * @property {function} error Log si error actif
 * @property {function} warning Log si warning actif
 * @property {function} setLogLevel
 * @property {an-logger-configurator} config
 * @param {*} Nombre variable d'arguments à passer aux renderers configurés pour ce channel
 */
/**
 * Configure un logger
 * @typedef an-logger-configurator
 * @type function
 * @param {an-logger-config}
 */
/**
 * Objet pour configurer un logger
 * @typedef an-logger-config
 * @type Object
 * @property {rendererConfig} [renderer]
 * @property {rendererConfig[]} [renderers] idem en array (ignoré si renderer fourni)
 */
/**
 * @typedef rendererConfig
 * @type Object
 * @property {string} name ansi|browser|cordova|file|remote|text
 * @property {string|number} [logLevel=info]
 * @property {string} [target] le nom du fichier pour le renderer file
 */
/**
 * Configure tous les loggers
 * @typedef an-loggers-configurator
 * @type function
 * @param {an-loggers-config}
 */
/**
 * Objet pour configurer les loggers (même ceux qui ne sont pas encore instanciés)
 * Les clés sont les noms des loggers, la valeur un objet an-logger-config
 * @typedef an-loggers-config
 * @type Object
 * @property {an-logger-config} nomDuLogger
 */
